/**
 * Community place-point tool (W.2 editor) — a Cesium ScreenSpaceEventHandler that
 * lets the operator drop a single point on the COP to DECLARE a new community or
 * MOVE an existing one's centroid. The single-point sibling of SectorDrawTool
 * (Task #64), which is the established map-interaction precedent.
 *
 *   MOUSE_MOVE    live crosshair preview at the cursor
 *   LEFT_CLICK    place the point → onPlaced([lon, lat]) and stop
 *   RIGHT_CLICK / ESC  cancel
 *
 * Imperative installer (MapView owns the viewer), mirroring the layer + draw-tool
 * installers. The preview marker lives in its own CustomDataSource so it never
 * pollutes the CommunitiesLayer. While active, MapView's main click handler
 * short-circuits (checks isActive()) so a place-click never opens a detail panel.
 */
import * as Cesium from 'cesium';
import { communityStatusStyle } from './styles/communityStatus';

export interface CommunityPlaceToolOptions {
  /** Fired with [lon, lat] when the operator clicks to place the point. */
  onPlaced: (lonLat: [number, number]) => void;
  /** Fired on cancel (RIGHT_CLICK / ESC / programmatic). */
  onCancel?: () => void;
  /** Fired whenever placing starts/stops (for UI / the toolbar button state). */
  onStateChange?: (active: boolean) => void;
}

export interface CommunityPlaceToolHandle {
  start(): void;
  cancel(): void;
  isActive(): boolean;
  destroy(): void;
}

export function installCommunityPlaceTool(
  viewer: Cesium.Viewer,
  opts: CommunityPlaceToolOptions,
): CommunityPlaceToolHandle {
  const ds = new Cesium.CustomDataSource('community-place');
  void viewer.dataSources.add(ds);

  let active = false;
  let pin: Cesium.Entity | null = null;
  let handler: Cesium.ScreenSpaceEventHandler | null = null;
  let keyListener: ((e: KeyboardEvent) => void) | null = null;

  // Neutral ambient slate — the colour a community with no wellness check yet
  // would paint (styles/communityStatus), so the preview reads as a community.
  const markerColor = Cesium.Color.fromCssColorString(communityStatusStyle(null).color);

  const screenToWorld = (pos: Cesium.Cartesian2): Cesium.Cartesian3 | null => {
    const ray = viewer.camera.getPickRay(pos);
    const ground =
      (ray && viewer.scene.globe.pick(ray, viewer.scene)) ||
      viewer.camera.pickEllipsoid(pos, viewer.scene.globe.ellipsoid);
    return ground ?? null;
  };

  const worldToLonLat = (c: Cesium.Cartesian3): [number, number] => {
    const carto = Cesium.Cartographic.fromCartesian(c);
    return [Cesium.Math.toDegrees(carto.longitude), Cesium.Math.toDegrees(carto.latitude)];
  };

  const renderScaffold = (): void => {
    ds.entities.removeAll();
    // A pin tracking the cursor; position is updated imperatively on MOUSE_MOVE.
    pin = ds.entities.add({
      show: false,
      point: {
        pixelSize: 14,
        color: markerColor.withAlpha(0.9),
        outlineColor: Cesium.Color.fromCssColorString('#0b1020'),
        outlineWidth: 2,
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
      },
    });
  };

  const stop = (): void => {
    active = false;
    if (handler) {
      handler.destroy();
      handler = null;
    }
    if (keyListener) {
      window.removeEventListener('keydown', keyListener);
      keyListener = null;
    }
    pin = null;
    ds.entities.removeAll();
    opts.onStateChange?.(false);
  };

  const cancel = (): void => {
    if (!active) return;
    stop();
    opts.onCancel?.();
  };

  const start = (): void => {
    if (active) return;
    active = true;
    renderScaffold();
    opts.onStateChange?.(true);

    handler = new Cesium.ScreenSpaceEventHandler(viewer.canvas);

    handler.setInputAction((movement: Cesium.ScreenSpaceEventHandler.MotionEvent) => {
      const world = screenToWorld(movement.endPosition);
      if (world && pin) {
        pin.position = new Cesium.ConstantPositionProperty(world);
        pin.show = true;
      }
    }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);

    handler.setInputAction((click: Cesium.ScreenSpaceEventHandler.PositionedEvent) => {
      const world = screenToWorld(click.position);
      if (!world) return;
      const ll = worldToLonLat(world);
      stop();
      opts.onPlaced(ll);
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

    handler.setInputAction(() => cancel(), Cesium.ScreenSpaceEventType.RIGHT_CLICK);

    keyListener = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') cancel();
    };
    window.addEventListener('keydown', keyListener);
  };

  return {
    start,
    cancel,
    isActive(): boolean {
      return active;
    },
    destroy(): void {
      stop();
      if (!viewer.isDestroyed()) viewer.dataSources.remove(ds, true);
    },
  };
}
